import { isEmpty } from "lodash-es";
import { useTranslation } from "react-i18next";

import { IActiveFilterData } from "@/data/filters";
import { formatFullName } from "@/data/persons";
import { ITasksURLFilter } from "@/data/tasks";
import { IUser } from "@/data/users";
import { useCodelistTranslation } from "@/hooks/Codelist";

export const useTasksActiveFilters = (
  filters: ITasksURLFilter,
  onFilterChange: (filter?: Partial<ITasksURLFilter> | undefined) => void,
  currentUser?: Partial<IUser>,
) => {
  const { t } = useTranslation("session");
  const tCodelist = useCodelistTranslation();

  const activeBadges: IActiveFilterData[] = [];
  let filtersCount = 0;

  if (filters.types && !isEmpty(filters.types)) {
    filtersCount++;
    const types = filters.types.map(type => tCodelist("task-types", type));
    activeBadges.push({
      icon: "tag",
      label: t("TASKS.FILTERS.ACTIVE_FILTERS.TASK_TYPE_LABEL", {
        value: types.join(", "),
        count: types.length,
      }),
      clearFilter: () => onFilterChange({ types: [] }),
    });
  }

  if (filters.priorities && !isEmpty(filters.priorities)) {
    filtersCount++;
    activeBadges.push({
      icon: "business-dev",
      label: t("TASKS.FILTERS.ACTIVE_FILTERS.TASK_URGENCY_LABEL", {
        value: filters.priorities.map(type => tCodelist("task-priorities", type)).join(", "),
      }),
      clearFilter: () => onFilterChange({ priorities: [] }),
    });
  }

  if (filters.assignees && !isEmpty(filters.assignees)) {
    filtersCount++;
    const assignees = filters.assignees.map(u => formatFullName(u));
    activeBadges.push({
      icon: "user",
      label: t("TASKS.FILTERS.ACTIVE_FILTERS.ASSIGNEE_LABEL", {
        count: assignees.length,
        value: assignees.join(", "),
      }),
      clearFilter: () => onFilterChange({ assignees: [] }),
    });
  }

  if (filters.originServices && !isEmpty(filters.originServices)) {
    filtersCount++;
    const originServices = filters.originServices.map(c => c.name);
    activeBadges.push({
      icon: "others",
      label: t("TASKS.FILTERS.ACTIVE_FILTERS.CUSTOMER_LABEL", {
        count: originServices.length,
        value: originServices.join(", "),
      }),
      clearFilter: () => onFilterChange({ originServices: [] }),
    });
  }

  if (filters.withAssignee) {
    filtersCount++;

    activeBadges.push({
      icon: "others",
      label: t("TASKS.FILTERS.ACTIVE_FILTERS.ASSIGNEE_LABEL", {
        count: 1,
        value: formatFullName(currentUser),
      }),
      clearFilter: () => onFilterChange({ withAssignee: undefined }),
    });
  }

  if (filters.withAssignee === false) {
    filtersCount++;

    activeBadges.push({
      icon: "others",
      label: t("TASKS.FILTERS.UNASSIGNED_TASKS_LABEL"),
      clearFilter: () => onFilterChange({ withAssignee: undefined }),
    });
  }

  if (filters.query && !isEmpty(filters.query)) {
    filtersCount++;
  }

  return { activeBadges, filtersCount };
};
